// src/pages/finance-portal/customers/CustomerDetail.js

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Paper, Button, Card, CardContent,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Chip, Alert, Skeleton, Grid, LinearProgress, Divider, IconButton, Tooltip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import PeopleIcon from '@mui/icons-material/People';
import ReceiptIcon from '@mui/icons-material/Receipt';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useParams, useNavigate } from 'react-router-dom';
import CustomerForm from './CustomerForm';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#22c55e';

const STATUS_COLORS = {
    ACTIVE: { bg: '#dcfce7', color: '#166534' },
    INACTIVE: { bg: '#f1f5f9', color: '#64748b' },
    ARCHIVED: { bg: '#fecaca', color: '#991b1b' },
};

const INVOICE_STATUS_COLORS = {
    DRAFT: { bg: '#f1f5f9', color: '#64748b' },
    SENT: { bg: '#dbeafe', color: '#1e40af' },
    VIEWED: { bg: '#e0e7ff', color: '#3730a3' },
    PARTIAL: { bg: '#fef3c7', color: '#92400e' },
    PAID: { bg: '#dcfce7', color: '#166534' },
    OVERDUE: { bg: '#fecaca', color: '#991b1b' },
    VOID: { bg: '#f1f5f9', color: '#94a3b8' },
};

function formatCurrency(val, currency = 'USD') {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(val || 0);
}

function formatDate(dateStr) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function formatAddress(addr) {
    if (!addr) return null;
    const a = typeof addr === 'string' ? JSON.parse(addr) : addr;
    const parts = [a.street, a.city, a.state, a.zip, a.country].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
}

export default function CustomerDetail() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [customer, setCustomer] = useState(null);
    const [balance, setBalance] = useState(null);
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [recentPayments, setRecentPayments] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Edit form
    const [formOpen, setFormOpen] = useState(false);

    const fetchCustomer = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [custRes, balRes, invRes, payRes] = await Promise.all([
                axiosInstance.get(`/api/finance/customers/${id}`),
                axiosInstance.get(`/api/finance/customers/${id}/balance-summary`),
                axiosInstance.get('/api/finance/invoices', { params: { customer_id: id, per_page: 5 } }),
                axiosInstance.get('/api/finance/payments', { params: { customer_id: id, per_page: 5 } }),
            ]);

            if (custRes.data.success) setCustomer(custRes.data.data);
            if (balRes.data.success) setBalance(balRes.data.data);
            if (invRes.data.success) setRecentInvoices(invRes.data.data);
            if (payRes.data.success) setRecentPayments(payRes.data.data);
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to load customer details');
        } finally {
            setIsLoading(false);
        }
    }, [id]);

    useEffect(() => { fetchCustomer(); }, [fetchCustomer]);

    const handleFormSaved = () => {
        fetchCustomer();
    };

    if (isLoading) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
                <Paper elevation={0} sx={{
                    background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Skeleton variant="circular" width={40} height={40} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton width={200} height={32} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                            <Skeleton width={120} height={20} sx={{ bgcolor: 'rgba(255,255,255,0.2)' }} />
                        </Box>
                    </Box>
                </Paper>
                <Box sx={{ px: { xs: 2, md: 4 }, maxWidth: 1400, mx: 'auto' }}>
                    {[1, 2, 3].map(i => <Skeleton key={i} height={180} sx={{ mb: 2, borderRadius: '16px' }} />)}
                </Box>
            </Box>
        );
    }

    if (error && !customer) {
        return (
            <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', p: 4 }}>
                <Alert severity="error" sx={{ borderRadius: '12px', fontFamily }}>
                    {error}
                    <Button onClick={() => navigate('/finance/customers')} sx={{ ml: 2, fontFamily, textTransform: 'none' }}>Back to Customers</Button>
                </Alert>
            </Box>
        );
    }

    if (!customer) return null;

    const displayName = customer.company_name
        ? customer.company_name
        : `${customer.first_name || ''} ${customer.last_name || ''}`.trim();

    const statusStyle = STATUS_COLORS[customer.status] || STATUS_COLORS.INACTIVE;

    const creditUtilization = balance && customer.credit_limit
        ? Math.min(((balance.total_outstanding || 0) / customer.credit_limit) * 100, 100)
        : 0;

    const creditWarning = customer.credit_limit_warning_pct || 80;
    const isCreditWarning = creditUtilization >= creditWarning;
    const isCreditExceeded = creditUtilization >= 100;

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* Header */}
            <Paper elevation={0} sx={{
                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                borderRadius: { xs: 0, md: '0 0 24px 24px' },
                px: { xs: 2.5, md: 4 }, py: { xs: 3, md: 4 }, mb: 3,
                position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -30, right: -30, width: 160, height: 160, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.06)' }} />
                <Box sx={{ position: 'relative', zIndex: 1 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate('/finance/customers')}
                        sx={{ fontFamily, textTransform: 'none', color: 'rgba(255,255,255,0.8)', mb: 1, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}
                    >
                        Back to Customers
                    </Button>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Box sx={{ p: 1.5, borderRadius: '14px', bgcolor: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <PeopleIcon sx={{ fontSize: 28, color: '#fff' }} />
                            </Box>
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Typography variant="h4" sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.4rem', md: '1.85rem' }, lineHeight: 1.2 }}>
                                        {displayName}
                                    </Typography>
                                    <Chip
                                        label={customer.status}
                                        size="small"
                                        sx={{
                                            fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                            bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                                        }}
                                    />
                                </Box>
                                <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', mt: 0.25 }}>
                                    {customer.customer_number || 'Customer'}
                                    {customer.payment_terms && ` \u00B7 ${customer.payment_terms.replace(/_/g, ' ')}`}
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={() => setFormOpen(true)}
                            sx={{
                                fontFamily, fontWeight: 600, textTransform: 'none', borderRadius: '10px',
                                bgcolor: 'rgba(255,255,255,0.2)', color: '#fff',
                                '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                            }}
                        >
                            Edit
                        </Button>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1400, mx: 'auto' }}>
                {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2, borderRadius: '12px', fontFamily }}>{error}</Alert>}

                <Grid container spacing={3}>
                    {/* Customer Info Card */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b', mb: 2 }}>
                                    Customer Information
                                </Typography>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {customer.company_name && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Company</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>{customer.company_name}</Typography>
                                        </Box>
                                    )}

                                    {(customer.first_name || customer.last_name) && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Contact</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>{customer.first_name} {customer.last_name}</Typography>
                                        </Box>
                                    )}

                                    {customer.email && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>{customer.email}</Typography>
                                        </Box>
                                    )}

                                    {customer.phone && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>{customer.phone}</Typography>
                                        </Box>
                                    )}

                                    <Divider />

                                    <Box sx={{ display: 'flex', gap: 3 }}>
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Payment Terms</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>
                                                {customer.payment_terms ? customer.payment_terms.replace(/_/g, ' ') : '-'}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Credit Limit</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>
                                                {customer.credit_limit ? formatCurrency(customer.credit_limit, customer.currency || 'USD') : 'Unlimited'}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {customer.tax_id && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Tax ID</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>{customer.tax_id}</Typography>
                                                {customer.tax_exempt && <Chip label="Tax Exempt" size="small" sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: '#fef3c7', color: '#92400e' }} />}
                                            </Box>
                                        </Box>
                                    )}

                                    {formatAddress(customer.billing_address) && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Billing Address</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>{formatAddress(customer.billing_address)}</Typography>
                                        </Box>
                                    )}

                                    {formatAddress(customer.shipping_address) && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Shipping Address</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.9rem', color: '#1e293b' }}>{formatAddress(customer.shipping_address)}</Typography>
                                        </Box>
                                    )}

                                    {customer.tags && customer.tags.length > 0 && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.5 }}>Tags</Typography>
                                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                                {(Array.isArray(customer.tags) ? customer.tags : []).map((tag, i) => (
                                                    <Chip key={i} label={tag} size="small" sx={{ fontFamily, fontSize: '0.7rem', height: 22, bgcolor: `${ACCENT}15`, color: ACCENT }} />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}

                                    {customer.notes && (
                                        <Box>
                                            <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</Typography>
                                            <Typography sx={{ fontFamily, fontSize: '0.85rem', color: '#64748b', whiteSpace: 'pre-wrap' }}>{customer.notes}</Typography>
                                        </Box>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Balance Summary Card */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                        Balance Summary
                                    </Typography>
                                    {isCreditWarning && (
                                        <Chip
                                            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
                                            label={isCreditExceeded ? 'Credit Exceeded' : 'Credit Warning'}
                                            size="small"
                                            sx={{
                                                fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 24,
                                                bgcolor: isCreditExceeded ? '#fecaca' : '#fef3c7',
                                                color: isCreditExceeded ? '#991b1b' : '#92400e',
                                                '& .MuiChip-icon': { color: 'inherit' },
                                            }}
                                        />
                                    )}
                                </Box>

                                {balance ? (
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                                        {/* Outstanding & Overdue */}
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Box sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <AccountBalanceWalletIcon sx={{ fontSize: 16, color: '#3b82f6' }} />
                                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Total Outstanding</Typography>
                                                </Box>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.3rem', color: '#1e293b' }}>
                                                    {formatCurrency(balance.total_outstanding, customer.currency || 'USD')}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ flex: 1, p: 2, borderRadius: '12px', bgcolor: balance.total_overdue > 0 ? 'rgba(239,68,68,0.04)' : '#f8fafc', border: `1px solid ${balance.total_overdue > 0 ? 'rgba(239,68,68,0.2)' : '#e2e8f0'}` }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <WarningAmberIcon sx={{ fontSize: 16, color: balance.total_overdue > 0 ? '#ef4444' : '#94a3b8' }} />
                                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>Total Overdue</Typography>
                                                </Box>
                                                <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1.3rem', color: balance.total_overdue > 0 ? '#ef4444' : '#1e293b' }}>
                                                    {formatCurrency(balance.total_overdue, customer.currency || 'USD')}
                                                </Typography>
                                            </Box>
                                        </Box>

                                        {/* Credit Utilization */}
                                        {customer.credit_limit > 0 && (
                                            <Box>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.75 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>Credit Utilization</Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 700, color: isCreditExceeded ? '#ef4444' : isCreditWarning ? '#f59e0b' : ACCENT }}>
                                                        {creditUtilization.toFixed(1)}%
                                                    </Typography>
                                                </Box>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={Math.min(creditUtilization, 100)}
                                                    sx={{
                                                        height: 8, borderRadius: 4,
                                                        bgcolor: '#e2e8f0',
                                                        '& .MuiLinearProgress-bar': {
                                                            borderRadius: 4,
                                                            bgcolor: isCreditExceeded ? '#ef4444' : isCreditWarning ? '#f59e0b' : ACCENT,
                                                        },
                                                    }}
                                                />
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                                        {formatCurrency(balance.total_outstanding, customer.currency || 'USD')} used
                                                    </Typography>
                                                    <Typography sx={{ fontFamily, fontSize: '0.7rem', color: '#94a3b8' }}>
                                                        {formatCurrency(customer.credit_limit, customer.currency || 'USD')} limit
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}

                                        {/* Credit Status */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#475569' }}>Credit Status:</Typography>
                                            <Chip
                                                label={isCreditExceeded ? 'Over Limit' : isCreditWarning ? 'Approaching Limit' : 'Good Standing'}
                                                size="small"
                                                sx={{
                                                    fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 22,
                                                    bgcolor: isCreditExceeded ? '#fecaca' : isCreditWarning ? '#fef3c7' : '#dcfce7',
                                                    color: isCreditExceeded ? '#991b1b' : isCreditWarning ? '#92400e' : '#166534',
                                                }}
                                            />
                                        </Box>

                                        <Divider />

                                        {/* Invoice Counts by Status */}
                                        {balance.invoice_counts && (
                                            <Box>
                                                <Typography sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#475569', mb: 1 }}>Invoices by Status</Typography>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                                    {Object.entries(balance.invoice_counts).map(([status, count]) => {
                                                        const style = INVOICE_STATUS_COLORS[status] || { bg: '#f1f5f9', color: '#64748b' };
                                                        return (
                                                            <Chip
                                                                key={status}
                                                                label={`${status}: ${count}`}
                                                                size="small"
                                                                sx={{ fontFamily, fontSize: '0.7rem', fontWeight: 600, height: 24, bgcolor: style.bg, color: style.color }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        )}
                                    </Box>
                                ) : (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.9rem' }}>No balance data available</Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Invoices */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <ReceiptIcon sx={{ fontSize: 20, color: ACCENT }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                        Recent Invoices
                                    </Typography>
                                </Box>

                                {recentInvoices.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No invoices yet</Typography>
                                    </Box>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                    {['Invoice #', 'Date', 'Amount', 'Status'].map(h => (
                                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>{h}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {recentInvoices.map(inv => {
                                                    const invStatus = INVOICE_STATUS_COLORS[inv.status] || { bg: '#f1f5f9', color: '#64748b' };
                                                    return (
                                                        <TableRow key={inv.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                            <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#1e293b' }}>{inv.invoice_number || '-'}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#475569' }}>{formatDate(inv.invoice_date || inv.created_at)}</TableCell>
                                                            <TableCell sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: '#1e293b' }}>{formatCurrency(inv.total, customer.currency || 'USD')}</TableCell>
                                                            <TableCell>
                                                                <Chip label={inv.status} size="small" sx={{ fontFamily, fontSize: '0.65rem', fontWeight: 600, height: 20, bgcolor: invStatus.bg, color: invStatus.color }} />
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Recent Payments */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                    <PaymentIcon sx={{ fontSize: 20, color: ACCENT }} />
                                    <Typography sx={{ fontFamily, fontWeight: 700, fontSize: '1rem', color: '#1e293b' }}>
                                        Recent Payments
                                    </Typography>
                                </Box>

                                {recentPayments.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 3 }}>
                                        <Typography sx={{ fontFamily, color: '#94a3b8', fontSize: '0.85rem' }}>No payments yet</Typography>
                                    </Box>
                                ) : (
                                    <TableContainer>
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                                                    {['Payment #', 'Date', 'Amount', 'Method'].map(h => (
                                                        <TableCell key={h} sx={{ fontFamily, fontWeight: 700, fontSize: '0.7rem', color: '#64748b', textTransform: 'uppercase', borderBottom: '2px solid #e2e8f0', letterSpacing: '0.05em' }}>{h}</TableCell>
                                                    ))}
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {recentPayments.map(pmt => (
                                                    <TableRow key={pmt.id} hover sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                        <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: '#1e293b' }}>{pmt.payment_number || '-'}</TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#475569' }}>{formatDate(pmt.payment_date || pmt.created_at)}</TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem', fontWeight: 600, color: ACCENT }}>{formatCurrency(pmt.amount, customer.currency || 'USD')}</TableCell>
                                                        <TableCell sx={{ fontFamily, fontSize: '0.8rem', color: '#64748b' }}>{pmt.payment_method ? pmt.payment_method.replace(/_/g, ' ') : '-'}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Edit Form Dialog */}
            <CustomerForm
                open={formOpen}
                onClose={() => setFormOpen(false)}
                onSaved={handleFormSaved}
                customer={customer}
            />
        </Box>
    );
}
